import { demoFunc } from './dees-harness-reasoning.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type { IHarnessMessageViewState, IHarnessReasoningPart } from '../interfaces.js';
import { harnessFormatDuration } from '../harness.helpers.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-reasoning': DeesHarnessReasoning;
  }
}

/**
 * Reasoning ("thinking") disclosure for a message's complete reasoning trace.
 * Collapsed it shows a one-line summary — the trace's first line, or the
 * latest line while streaming — and expanding reveals the FULL concatenated
 * trace of every part in one flat body. Nothing opens on its own: a live trace
 * keeps streaming into the collapsed preview line, and the reader's
 * expand/collapse choice survives re-renders because it lives on this element,
 * not in the parent's template.
 */
@customElement('dees-harness-reasoning')
export class DeesHarnessReasoning extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor reasoningParts: IHarnessReasoningPart[] = [];

  @property({ attribute: false })
  accessor viewState: IHarnessMessageViewState | undefined = undefined;

  @state()
  accessor expanded: boolean | undefined = undefined;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
      }

      .reasoning {
        border: 0;
        background: transparent;
        overflow: hidden;
      }

      /* Expand/collapse animates through the 0fr→1fr grid trick so the body
         stays in the DOM and both directions transition smoothly. */
      .bodyWrap {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows var(--dees-transition-default) var(--dees-ease-standard);
      }

      .bodyWrap.open {
        grid-template-rows: 1fr;
      }

      .bodyWrap > .bodyClip {
        overflow: hidden;
        min-height: 0;
      }

      @media (prefers-reduced-motion: reduce) {
        .bodyWrap,
        .chevron {
          transition: none;
        }
      }

      .summary {
        width: 100%;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) 0;
        border: 0;
        background: transparent;
        color: var(--dees-color-text-secondary);
        cursor: pointer;
        font-family: inherit;
        font-size: 12px;
        font-weight: 500;
        text-align: left;
      }

      .summary:hover {
        color: var(--dees-color-text-primary);
      }

      .chevron {
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        transition: transform var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .chevron.open {
        transform: rotate(90deg);
      }

      .label {
        flex: 0 0 auto;
      }

      .preview {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--dees-color-text-muted);
        font-weight: 400;
        font-style: italic;
      }

      .duration {
        margin-left: auto;
        flex: 0 0 auto;
        color: var(--dees-color-text-muted);
        font-weight: 400;
      }

      .thinkingDots::after {
        content: '';
        display: inline-block;
        width: 1.2em;
        text-align: left;
        animation: harness-reasoning-dots 1.2s steps(4, end) infinite;
      }

      @keyframes harness-reasoning-dots {
        0% { content: ''; }
        25% { content: '.'; }
        50% { content: '..'; }
        75%, 100% { content: '...'; }
      }

      .body {
        margin: 0;
        padding: 0 0 var(--dees-spacing-sm) calc(12px + var(--dees-spacing-sm));
        color: var(--dees-color-text-secondary);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        font-size: var(--dees-font-caption1-size, 11px);
        line-height: 1.5;
      }
    `,
  ];

  public get isStreaming(): boolean {
    return this.reasoningParts.some((part) => Boolean(part.startedAt) && !part.endedAt);
  }

  public get isOpen(): boolean {
    return this.expanded ?? this.viewState?.reasoningExpanded ?? false;
  }

  /** All parts joined into the one full trace the expanded body shows. */
  public get fullText(): string {
    return this.reasoningParts
      .map((part) => part.text.trim())
      .filter((text) => text.length > 0)
      .join('\n\n');
  }

  /**
   * One line for the collapsed state: the latest line while streaming (live
   * progress), the first line once finished (the trace's own summary).
   */
  public get previewLine(): string {
    const lines = this.fullText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (lines.length === 0) return '';
    const line = this.isStreaming ? lines[lines.length - 1] : lines[0];
    // Reasoning summaries often arrive as markdown headings; the one-line
    // preview shows plain text.
    return line
      .replace(/^#+\s*/, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/`([^`]+)`/g, '$1');
  }

  public render(): TemplateResult {
    const startedAt = this.reasoningParts.find((part) => part.startedAt)?.startedAt;
    const endedAt = this.isStreaming
      ? undefined
      : [...this.reasoningParts].reverse().find((part) => part.endedAt)?.endedAt;
    const duration = harnessFormatDuration(startedAt, endedAt);
    const preview = this.previewLine;
    return html`
      <div class="reasoning">
        <button
          class="summary"
          type="button"
          aria-expanded=${this.isOpen ? 'true' : 'false'}
          @click=${() => {
            this.expanded = !this.isOpen;
            if (this.viewState) this.viewState.reasoningExpanded = this.expanded;
          }}
        >
          <span class="chevron ${this.isOpen ? 'open' : ''}">
            <dees-icon icon="lucide:ChevronRight" iconSize="12"></dees-icon>
          </span>
          <span class="label ${this.isStreaming ? 'thinkingDots' : ''}">
            ${this.isStreaming ? 'Thinking' : 'Thought'}
          </span>
          ${!this.isOpen && preview ? html`<span class="preview">${preview}</span>` : ''}
          ${duration ? html`<span class="duration">${duration}</span>` : ''}
        </button>
        <div class="bodyWrap ${this.isOpen ? 'open' : ''}" aria-hidden=${this.isOpen ? 'false' : 'true'}>
          <div class="bodyClip">
            <pre class="body">${this.fullText || (this.isStreaming ? 'Waiting for reasoning…' : 'No reasoning emitted.')}</pre>
          </div>
        </div>
      </div>
    `;
  }
}
